"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowLeft, Home } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "cn";

/**
 * 404 as a library catalog card: double-ruled border, blind-stamped inner
 * frame, serif type, and the status as a rubber stamp rather than a colour.
 */
export default function NotFound() {
  const router = useRouter();

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4 text-foreground sm:p-8">
      <section
        aria-labelledby="not-found-title"
        className={cn(
          "relative w-full max-w-4xl overflow-hidden rounded-lg",
          "border-4 border-double border-border bg-card text-card-foreground shadow-lg",
        )}
      >
        {/* Blind-stamped frame, inset from the card edge */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-3 rounded-md border border-foreground/15"
        />

        <div className="relative flex flex-col gap-8 px-8 pt-12 pb-20 sm:px-16 sm:pt-16">
          <p
            aria-hidden="true"
            className="border-b-4 border-double border-input pb-3 font-serif text-sm text-muted-foreground uppercase"
          >
            Каталог
          </p>

          <div className="space-y-4">
            <p
              aria-hidden="true"
              className="font-serif text-8xl leading-none font-bold tracking-tight sm:text-9xl"
            >
              404
            </p>
            <h1
              id="not-found-title"
              className="font-serif text-4xl leading-tight font-semibold text-balance sm:text-5xl"
            >
              Страница не найдена
            </h1>
          </div>

          <p className="border-t border-input pt-6 font-serif text-xl text-muted-foreground italic sm:text-2xl">
            Я не знаю, куда ты лезешь, но этого нет
            <span className="mt-1 block text-base">
              (или у тебя нет на это прав)
            </span>
          </p>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              variant="outline"
              size="lg"
              className="h-12 sm:flex-1"
              onClick={() => router.back()}
            >
              <ArrowLeft aria-hidden="true" />
              Вернуться обратно
            </Button>
            <Link
              href="/"
              className={cn(
                buttonVariants({ size: "lg" }),
                "h-12 text-base sm:flex-1",
              )}
            >
              <Home aria-hidden="true" />
              На главную
            </Link>
          </div>
        </div>

        {/* Rubber stamp */}
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute top-14 right-8 -rotate-12 sm:top-20 sm:right-16",
            "rounded-xs border-4 border-destructive px-5 py-2",
            "font-serif text-xl font-bold text-destructive uppercase sm:text-3xl",
            "outline-2 -outline-offset-8 outline-destructive/60",
            "opacity-85 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-125 motion-safe:duration-300",
          )}
        >
          Не найдено
        </div>
      </section>
    </main>
  );
}
